import type {Repository} from './repo'

/** Loader shape the repo/file pickers call to fetch repositories for a query. */
export type RepositoryLoader = (query: string, signal?: AbortSignal) => Promise<Repository[]>

/** Owner login parsed from a `owner/repo` repository name. */
export function repositoryOwner(repo: Repository): string {
  return repo.name.split('/')[0] ?? ''
}

/**
 * Returns true when a repository is valid under the active scope owner.
 *
 * With an undefined scope owner every repository is in scope; null or empty
 * means an unresolved owner and excludes all repositories. With a scope owner,
 * public repositories remain attachable while private repositories must belong
 * to that account (case-insensitive).
 */
export function isRepositoryInScope(repo: Repository, scopeOwner: string | null | undefined): boolean {
  if (scopeOwner === undefined) return true
  if (!scopeOwner) return false
  return !repo.private || repositoryOwner(repo).toLowerCase() === scopeOwner.toLowerCase()
}

/**
 * Prefixes a search query with the scope owner so the backend can prioritize
 * in-scope repositories. Queries that already target an owner (contain `/`) are
 * left untouched.
 *
 * A blank query is also left untouched. Both repository endpoints treat a blank
 * query as "return the viewer's top repositories", ranked by recent activity;
 * prefixing turns it into an owner listing, which is ordered arbitrarily and
 * ignores the pre-selected repository. Scope is enforced by filtering results,
 * not by rewriting a blank query. Whitespace-only input counts as blank because
 * the backend trims before matching, so `<owner>/   ` would reach that same
 * owner-listing branch.
 */
export function scopedRepositoryQuery(query: string, scopeOwner: string | null | undefined): string {
  if (!query.trim()) return query
  return scopeOwner && !query.includes('/') ? `${scopeOwner}/${query}` : query
}

/**
 * Wraps a repository loader so private results are restricted to the active scope owner.
 *
 * Typed queries without an owner search both the scoped owner and globally so
 * in-scope repositories are not lost from capped results and public repositories
 * remain discoverable. The client-side filter is the authoritative guard that
 * prevents private repositories from another owner being attached. Blank and
 * owner-prefixed queries use one request; blank-query owner prioritization is
 * handled by the underlying loader. When `scopeOwner` is undefined the original
 * loader is returned unchanged. A null or empty owner blocks searching until
 * the scoped owner resolves.
 */
export function createScopedRepositoryLoader(
  loader: RepositoryLoader,
  scopeOwner: string | null | undefined,
): RepositoryLoader {
  if (scopeOwner === undefined) return loader
  if (!scopeOwner) return async () => []
  return async (query, signal) => {
    const trimmedQuery = query.trim()
    const scopedQuery = scopedRepositoryQuery(trimmedQuery, scopeOwner)
    if (!trimmedQuery || query.includes('/')) {
      const repos = await loader(query, signal)
      return repos.filter(repo => isRepositoryInScope(repo, scopeOwner))
    }

    const [scoped, global] = await Promise.allSettled([loader(scopedQuery, signal), loader(query, signal)])
    if (signal?.aborted) throw signal.reason
    if (scoped.status === 'rejected' && global.status === 'rejected') throw global.reason

    const repos = [
      ...(scoped.status === 'fulfilled' ? scoped.value : []),
      ...(global.status === 'fulfilled' ? global.value : []),
    ]
    const seen = new Set<Repository['id']>()
    return repos.filter(repo => {
      if (seen.has(repo.id)) return false
      seen.add(repo.id)
      return isRepositoryInScope(repo, scopeOwner)
    })
  }
}
